import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, FileSpreadsheet, Search, Save, Pencil, Printer, Trophy } from "lucide-react";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TERMS = ["Term 1", "Term 2", "Term 3"];

export default function ResultsPage() {
  // Dynamic subjects (fetched from DB based on class grade level)
  const [classSubjects, setClassSubjects] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);

  // Bulk Add Results state (per student, all subjects in one row)
  const [showBulkResults, setShowBulkResults] = useState(false);
  const [bulkClass, setBulkClass] = useState("");
  const [bulkTerm, setBulkTerm] = useState("");
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear().toString());
  const [bulkRows, setBulkRows] = useState([
    { id: 1, student_id: '', marks: {} }
  ]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResultMsg, setBulkResultMsg] = useState(null);

  // Get user from context or props (ensure it's defined before use)
  const { user } = useAuth();
  let isAdmin = false;
  let isTeacher = false;
  if (typeof user !== 'undefined' && user) {
    isAdmin = user.role === 'admin';
    isTeacher = user.role === 'teacher';
  }
  const [results, setResults] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  
  // Admin mode state
  const [formData, setFormData] = useState({
    student_id: "",
    subject: "",
    score: "",
    max_score: "100",
    term: "",
    academic_year: new Date().getFullYear().toString(),
  });

  // Teacher mode state
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [teacherFormData, setTeacherFormData] = useState({});

  // Admin class rankings state
  const [rankingsClass, setRankingsClass] = useState("");
  const [rankingsTerm, setRankingsTerm] = useState("");
  const [rankingsData, setRankingsData] = useState([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);

  useEffect(() => {
    fetchResults();
    fetchStudents();
    fetchClasses();
    fetchAllSubjects();
    // For teachers, auto-set term/year from active settings
    if (isTeacher) {
      axios.get(`${API}/settings/active-term`).then(res => {
        setSelectedTerm(res.data.active_term || "");
        setSelectedYear(res.data.academic_year || new Date().getFullYear().toString());
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For teachers: auto-fetch subjects when students load (they only see one class)
  useEffect(() => {
    if (isTeacher && students.length > 0 && classes.length > 0) {
      const studentClassId = students[0]?.class_id;
      if (studentClassId) {
        fetchSubjectsForClass(studentClassId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students, classes]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/results`);
      setResults(Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.results) ? response.data.results : []));
    } catch (error) {
      toast.error("Failed to fetch results");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`);
      setStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch students");
      setStudents([]);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch classes");
      setClasses([]);
    }
  };

  const fetchAllSubjects = async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      const subjectList = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.subjects) ? response.data.subjects : []);
      setAllSubjects(subjectList);
    } catch (error) {
      console.error("Failed to fetch subjects");
      setAllSubjects([]);
    }
  };

  // Fetch subjects for a class based on its grade_level
  const fetchSubjectsForClass = async (classId) => {
    try {
      const cls = classes.find((c) => c.id === classId || c.id?.toString() === classId);
      if (!cls || !cls.grade_level) {
        setClassSubjects([]);
        return [];
      }
      const response = await axios.get(`${API}/subjects/for-grade/${encodeURIComponent(cls.grade_level)}`);
      const subjectRows = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.subjects) ? response.data.subjects : []);
      const subjectNames = subjectRows.map((s) => s.name);
      setClassSubjects(subjectNames);
      return subjectNames;
    } catch (error) {
      console.error("Failed to fetch subjects for class");
      setClassSubjects([]);
      return [];
    }
  };

  const fetchClassRankings = async (classId, term) => {
    if (!classId || !term) return;
    setRankingsLoading(true);
    try {
      const response = await axios.get(`${API}/results/class-positions/${classId}/${encodeURIComponent(term)}`);
      setRankingsData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error("Failed to fetch class rankings");
      setRankingsData([]);
    } finally {
      setRankingsLoading(false);
    }
  };

  // Admin mode: Add single result
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/results`, {
        ...formData,
        score: parseFloat(formData.score),
        max_score: parseFloat(formData.max_score),
      });
      toast.success("Result added successfully");
      setIsModalOpen(false);
      resetForm();
      fetchResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add result");
    }
  };

  // Teacher mode: Handle form input change
  const handleTeacherInputChange = (subject, value) => {
    setTeacherFormData(prev => ({
      ...prev,
      [subject]: value
    }));
  };

  // Teacher mode: Save all results
  const handleTeacherSaveResults = async () => {
    if (!selectedStudent || !selectedTerm || !selectedYear) {
      toast.error("Please select student, term, and year");
      return;
    }

    try {
      const resultsToSave = classSubjects
        .filter(subject => teacherFormData[subject])
        .map(subject => ({
          student_id: selectedStudent,
          subject,
          score: parseFloat(teacherFormData[subject]) || 0,
          max_score: "100",
          term: selectedTerm,
          academic_year: selectedYear
        }));

      if (resultsToSave.length === 0) {
        toast.error("Please enter at least one score");
        return;
      }

      for (const result of resultsToSave) {
        await axios.post(`${API}/results`, result);
      }

      toast.success(`Added ${resultsToSave.length} results successfully`);
      setTeacherFormData({});
      setSelectedStudent("");
      fetchResults();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save results");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this result?")) return;
    try {
      await axios.delete(`${API}/results/${id}`);
      toast.success("Result deleted successfully");
      fetchResults();
    } catch (error) {
      toast.error("Failed to delete result");
    }
  };

  const resetForm = () => {
    setFormData({
      student_id: "",
      subject: "",
      score: "",
      max_score: "100",
      term: "",
      academic_year: new Date().getFullYear().toString(),
    });
  };

  const getUnitGrade = (percentage) => {
    if (percentage >= 90) return 1;
    if (percentage >= 80) return 2;
    if (percentage >= 70) return 3;
    if (percentage >= 60) return 4;
    if (percentage >= 50) return 5;
    return 6;
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 1:
        return "bg-emerald-50 text-emerald-700";
      case 2:
        return "bg-blue-50 text-blue-700";
      case 3:
        return "bg-amber-50 text-amber-700";
      case 4:
        return "bg-orange-50 text-orange-700";
      case 5:
        return "bg-gray-50 text-gray-700";
      default:
        return "bg-red-50 text-red-700";
    }
  };

  const filteredResults = results.filter((result) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      result.student_name?.toLowerCase().includes(searchLower) ||
      result.student_id_code?.toLowerCase().includes(searchLower) ||
      result.subject?.toLowerCase().includes(searchLower);

    if (!matchesSearch) return false;

    // Filter by class if admin selected a class
    if (user?.role === "admin" && selectedClass) {
      const student = students.find((s) => s.id === result.student_id);
      return student?.class_id === selectedClass;
    }

    return true;
  });

  const handlePrint = () => {
    window.print();
  };

  // TEACHER VIEW
  if (user?.role === "teacher") {
    // Compute class summary: group results by student, calc average, sort by position
    const termResults = results.filter(r =>
      (!selectedTerm || r.term === selectedTerm) &&
      (!selectedYear || r.academic_year?.toString() === selectedYear)
    );

    // Group by student
    const studentMap = {};
    termResults.forEach(r => {
      if (!studentMap[r.student_id]) {
        studentMap[r.student_id] = {
          student_id: r.student_id,
          student_name: r.student_name || 'Unknown',
          student_id_code: r.student_id_code || '',
          results: [],
        };
      }
      studentMap[r.student_id].results.push(r);
    });

    // Calculate averages and sort by position
    const classSummary = Object.values(studentMap).map(s => {
      const totalScore = s.results.reduce((sum, r) => sum + Number(r.score || 0), 0);
      const totalMax = s.results.reduce((sum, r) => sum + Number(r.max_score || 100), 0);
      const average = totalMax > 0 ? (totalScore / totalMax) * 100 : 0;
      return { ...s, average, subjectCount: s.results.length };
    }).sort((a, b) => b.average - a.average);

    // Assign positions
    classSummary.forEach((s, idx) => { s.position = idx + 1; });

    // Edit dialog handler
    const openEditDialog = (studentSummary) => {
      setSelectedStudent(studentSummary.student_id);
      // Pre-populate teacherFormData with existing scores
      const existingScores = {};
      studentSummary.results.forEach(r => {
        existingScores[r.subject] = r.score?.toString() || '';
      });
      setTeacherFormData(existingScores);
      setIsModalOpen(true);
    };

    const handleEditSave = async () => {
      if (!selectedStudent) return;
      const term = selectedTerm || 'Term 1';
      const year = selectedYear || new Date().getFullYear().toString();

      try {
        const resultsToSave = classSubjects
          .filter(subject => teacherFormData[subject] !== '' && teacherFormData[subject] !== undefined)
          .map(subject => ({
            student_id: selectedStudent,
            subject,
            score: parseFloat(teacherFormData[subject]) || 0,
            max_score: 100,
            term,
            academic_year: year
          }));

        if (resultsToSave.length === 0) {
          toast.error("Please enter at least one score");
          return;
        }

        for (const result of resultsToSave) {
          await axios.post(`${API}/results`, result);
        }

        toast.success(`Saved ${resultsToSave.length} results`);
        setIsModalOpen(false);
        setSelectedStudent('');
        setTeacherFormData({});
        fetchResults();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to save results");
      }
    };

    return (
      <div data-testid="results-page-teacher">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
              <FileSpreadsheet className="text-pink-500" size={32} />
              Class Results
            </h1>
            <p className="text-slate-500 mt-1">View class performance and edit student results</p>
          </div>
        </div>

        {/* Enter Results */}
        <Card className="border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Plus size={20} className="text-pink-500" />
              Enter Student Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Select Student</Label>
                  <Select value={selectedStudent} onValueChange={(val) => { setSelectedStudent(val); setTeacherFormData({}); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a student" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.student_id_code} - {student.first_name} {student.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Term</Label>
                  {isTeacher ? (
                    <Input value={selectedTerm} disabled className="bg-slate-50" />
                  ) : (
                  <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select term" />
                    </SelectTrigger>
                    <SelectContent>
                      {TERMS.map((term) => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Academic Year</Label>
                  <Input type="number" value={selectedYear} onChange={(e) => !isTeacher && setSelectedYear(e.target.value)} placeholder="2026" disabled={isTeacher} className={isTeacher ? "bg-slate-50" : ""} />
                </div>
              </div>

              {selectedStudent ? (
                <div>
                  <Label className="block mb-3">Enter Scores (out of 100)</Label>
                  <div className="border rounded-lg overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-medium text-slate-600">Subject</TableHead>
                          <TableHead className="font-medium text-slate-600">Score</TableHead>
                          <TableHead className="font-medium text-slate-600">Grade</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {classSubjects.map((subject) => {
                          const val = parseFloat(teacherFormData[subject]) || 0;
                          return (
                            <TableRow key={subject}>
                              <TableCell className="font-medium text-slate-900">{subject}</TableCell>
                              <TableCell>
                                <Input type="number" min="0" max="100" placeholder="0"
                                  value={teacherFormData[subject] || ""}
                                  onChange={(e) => handleTeacherInputChange(subject, e.target.value)}
                                  className="w-24" />
                              </TableCell>
                              <TableCell>
                                {teacherFormData[subject] && (
                                  <Badge className={getGradeColor(getUnitGrade(val))}>
                                    Unit {getUnitGrade(val)}
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-6 flex gap-3">
                    <Button onClick={handleTeacherSaveResults} className="bg-pink-500 hover:bg-pink-600 text-white">
                      <Save size={18} className="mr-2" />
                      Save All Results
                    </Button>
                    <Button variant="outline" onClick={() => { setTeacherFormData({}); setSelectedStudent(""); }}>
                      Clear
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <p>Select a student to enter results</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Class Summary Table */}
        <Card className="border-slate-200 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">
              Class Rankings ({classSummary.length} students)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
              </div>
            ) : classSummary.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileSpreadsheet size={48} className="mx-auto mb-4 opacity-50" />
                <p>No results found for the selected term</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-medium text-slate-500 w-16">Position</TableHead>
                      <TableHead className="font-medium text-slate-500">Student ID</TableHead>
                      <TableHead className="font-medium text-slate-500">Student Name</TableHead>
                      <TableHead className="font-medium text-slate-500">Subjects</TableHead>
                      <TableHead className="font-medium text-slate-500">Average %</TableHead>
                      <TableHead className="font-medium text-slate-500">Grade</TableHead>
                      <TableHead className="font-medium text-slate-500 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classSummary.map((s) => (
                      <TableRow
                        key={s.student_id}
                        className="table-row-hover cursor-pointer"
                        onClick={() => openEditDialog(s)}
                      >
                        <TableCell className="font-bold text-slate-900 text-center">
                          {s.position === 1 ? '🥇' : s.position === 2 ? '🥈' : s.position === 3 ? '🥉' : s.position}
                        </TableCell>
                        <TableCell className="font-mono text-slate-600 text-sm">
                          {s.student_id_code}
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          {s.student_name}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {s.subjectCount}
                        </TableCell>
                        <TableCell className="font-mono font-bold text-slate-900">
                          {s.average.toFixed(1)}%
                        </TableCell>
                        <TableCell>
                          <Badge className={getGradeColor(getUnitGrade(s.average))}>
                            Unit {getUnitGrade(s.average)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm"
                            onClick={(e) => { e.stopPropagation(); openEditDialog(s); }}
                            className="text-pink-500 hover:text-pink-600 hover:bg-pink-50">
                            <Pencil size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Student Results Dialog */}
        <Dialog open={isModalOpen} onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) { setSelectedStudent(''); setTeacherFormData({}); }
        }}>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Pencil size={20} className="text-pink-500" />
                Edit Student Results
              </DialogTitle>
              <DialogDescription>
                {selectedStudent && (() => {
                  const st = students.find(s => s.id === selectedStudent);
                  return st ? `${st.student_id_code} — ${st.first_name} ${st.last_name}` : '';
                })()}
                {selectedTerm && ` • ${selectedTerm}`} {selectedYear && ` ${selectedYear}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {!selectedTerm && (
                <div className="space-y-2">
                  <Label>Term</Label>
                  <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                    <SelectTrigger><SelectValue placeholder="Select term" /></SelectTrigger>
                    <SelectContent>
                      {TERMS.map((term) => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-600">Subject</TableHead>
                      <TableHead className="font-medium text-slate-600 w-24">Score</TableHead>
                      <TableHead className="font-medium text-slate-600 w-16">Grade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classSubjects.map((subject) => {
                      const val = parseFloat(teacherFormData[subject]) || 0;
                      return (
                        <TableRow key={subject}>
                          <TableCell className="font-medium text-slate-900 text-sm">{subject}</TableCell>
                          <TableCell>
                            <Input type="number" min="0" max="100" placeholder="0"
                              value={teacherFormData[subject] || ""}
                              onChange={(e) => handleTeacherInputChange(subject, e.target.value)}
                              className="w-20 text-sm" />
                          </TableCell>
                          <TableCell>
                            {teacherFormData[subject] && (
                              <Badge className={getGradeColor(getUnitGrade(val))}>
                                {getUnitGrade(val)}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => { setIsModalOpen(false); setSelectedStudent(''); setTeacherFormData({}); }}>
                  Cancel
                </Button>
                <Button onClick={handleEditSave} className="bg-pink-500 hover:bg-pink-600 text-white">
                  <Save size={18} className="mr-2" /> Save Results
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ADMIN VIEW
  return (
    <div data-testid="results-page">
      {/* Collapsible Bulk Add Results */}
      <div className="mb-8">
        <Button
          variant="outline"
          className="mb-2"
          onClick={() => setShowBulkResults((v) => !v)}
        >
          {showBulkResults ? 'Hide Bulk Add Results' : 'Bulk Add Results'}
        </Button>
        {showBulkResults && (
          <Card className="border-slate-200 mb-4">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Bulk Add Results</CardTitle>
              <p className="text-sm text-muted-foreground">
                {isAdmin
                  ? 'Select class, term, and year, then enter marks for each student in one row. Fields marked with * are required.'
                  : 'Enter results for your assigned students. Fields marked with * are required.'}
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                {isAdmin && (
                  <div>
                    <Label>Class *</Label>
                    <Select value={bulkClass} onValueChange={(val) => { setBulkClass(val); fetchSubjectsForClass(val); setBulkRows([{ id: 1, student_id: '', marks: {} }]); }}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select class" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map(cls => (
                          <SelectItem key={cls.id} value={cls.id.toString()}>{cls.display_name || `${cls.grade_level} ${cls.name}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Term *</Label>
                  <Select value={bulkTerm} onValueChange={setBulkTerm}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Select term" />
                    </SelectTrigger>
                    <SelectContent>
                      {TERMS.map(term => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year *</Label>
                  <Input
                    className="w-28"
                    value={bulkYear}
                    onChange={e => setBulkYear(e.target.value)}
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700 min-w-[120px]">Student *</TableHead>
                      {classSubjects.map(subject => (
                        <TableHead key={subject} className="font-medium text-slate-700 min-w-[100px]">{subject}</TableHead>
                      ))}
                      <TableHead className="font-medium text-slate-700 w-[50px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkRows.map((row, idx) => (
                      <TableRow key={row.id} className="table-row-hover">
                        <TableCell>
                          <Select
                            value={row.student_id}
                            onValueChange={value => setBulkRows(rs => rs.map((r, i) => i === idx ? { ...r, student_id: value } : r))}
                          >
                            <SelectTrigger className="text-sm border-slate-200 focus:border-pink-500">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {(isAdmin
                                ? students.filter(s => bulkClass ? s.class_id?.toString() === bulkClass : true)
                                : students // teachers only see their assigned students in students array
                              ).map(student => (
                                <SelectItem key={student.id} value={student.id.toString()}>{student.student_id_code} - {student.first_name} {student.last_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        {classSubjects.map(subject => (
                          <TableCell key={subject}>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="0"
                              value={row.marks[subject] || ''}
                              onChange={e => setBulkRows(rs => rs.map((r, i) => i === idx ? { ...r, marks: { ...r.marks, [subject]: e.target.value } } : r))}
                              className="text-sm border-slate-200 focus:border-pink-500 w-20"
                            />
                          </TableCell>
                        ))}
                        <TableCell>
                          <button
                            onClick={() => setBulkRows(rs => rs.length === 1 ? rs : rs.filter((_, i) => i !== idx))}
                            className="text-slate-400 hover:text-red-500 transition-colors p-1"
                            title="Remove row"
                          >
                            <Trash2 size={18} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setBulkRows(rs => [...rs, { id: Date.now(), student_id: '', marks: {} }])}
                  variant="outline"
                  className="flex items-center gap-2 border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  <Plus size={18} /> Add More
                </Button>
                <Button
                  onClick={async () => {
                    if ((isAdmin && !bulkClass) || !bulkTerm || !bulkYear) {
                      toast.error(isAdmin ? 'Please select class, term, and year' : 'Please select term and year');
                      return;
                    }
                    const validRows = bulkRows.filter(r => r.student_id && Object.values(r.marks).some(m => m));
                    if (validRows.length === 0) {
                      toast.error('Please fill in at least one mark for a student');
                      return;
                    }
                    setBulkSubmitting(true);
                    try {
                      let successCount = 0;
                      let errors = [];
                      for (const row of validRows) {
                        for (const subject of classSubjects) {
                          const score = row.marks[subject];
                          if (score !== '' && score !== undefined) {
                            try {
                              await axios.post(`${API}/results`, {
                                student_id: row.student_id,
                                subject,
                                score: parseFloat(score),
                                max_score: 100,
                                term: bulkTerm,
                                academic_year: bulkYear
                              });
                              successCount++;
                            } catch (err) {
                              errors.push({ error: err.response?.data?.detail || `Failed to add result for ${subject}` });
                            }
                          }
                        }
                      }
                      setBulkResultMsg({ successCount, errors });
                      toast.success(`Added ${successCount} results`);
                      setBulkRows([{ id: 1, student_id: '', marks: {} }]);
                      fetchResults();
                    } catch (error) {
                      toast.error('Bulk add failed');
                    } finally {
                      setBulkSubmitting(false);
                    }
                  }}
                  disabled={bulkSubmitting}
                  className="bg-pink-500 hover:bg-pink-600 btn-press ml-auto"
                >
                  {bulkSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Adding Results...
                    </span>
                  ) : (
                    'Add Results'
                  )}
                </Button>
              </div>
              {/* Results Section */}
              {bulkResultMsg && (
                <Card className="border-slate-200 mt-6">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      Bulk Add Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-emerald-600 font-medium">Added {bulkResultMsg.successCount} results</p>
                      {bulkResultMsg.errors && bulkResultMsg.errors.length > 0 && (
                        <div>
                          <h4 className="font-medium text-red-600 flex items-center gap-2 mb-2">
                            <span>Errors ({bulkResultMsg.errors.length}):</span>
                          </h4>
                          <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                            {bulkResultMsg.errors.map((err, idx) => (
                              <p key={idx} className="text-sm text-red-600">{err.error}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
            <FileSpreadsheet className="text-pink-500" size={32} />
            Results Management
          </h1>
          <p className="text-slate-500 mt-1">Enter and manage student results</p>
        </div>

        <Dialog open={isModalOpen} onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button
              className="bg-pink-500 hover:bg-pink-600 text-white btn-press"
              data-testid="add-result-btn"
            >
              <Plus size={18} className="mr-2" />
              Add Result
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Add New Result</DialogTitle>
              <DialogDescription>
                Enter a student's result for a subject
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Student</Label>
                <Select
                  value={formData.student_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, student_id: value })
                  }
                  required
                >
                  <SelectTrigger data-testid="result-student">
                    <SelectValue placeholder="Select a student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.student_id_code} - {student.first_name} {student.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subject</Label>
                <Select
                  value={formData.subject}
                  onValueChange={(value) =>
                    setFormData({ ...formData, subject: value })
                  }
                  required
                >
                  <SelectTrigger data-testid="result-subject">
                    <SelectValue placeholder="Select a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {allSubjects.map((subject) => (
                      <SelectItem key={subject.id} value={subject.name}>
                        {subject.name} ({subject.grade_group})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Score</Label>
                  <Input
                    type="number"
                    value={formData.score}
                    onChange={(e) =>
                      setFormData({ ...formData, score: e.target.value })
                    }
                    placeholder="85"
                    min="0"
                    max={formData.max_score}
                    required
                    data-testid="result-score"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Score</Label>
                  <Input
                    type="number"
                    value={formData.max_score}
                    onChange={(e) =>
                      setFormData({ ...formData, max_score: e.target.value })
                    }
                    placeholder="100"
                    min="1"
                    required
                    data-testid="result-max-score"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Term</Label>
                  <Select
                    value={formData.term}
                    onValueChange={(value) =>
                      setFormData({ ...formData, term: value })
                    }
                    required
                  >
                    <SelectTrigger data-testid="result-term">
                      <SelectValue placeholder="Select term" />
                    </SelectTrigger>
                    <SelectContent>
                      {TERMS.map((term) => (
                        <SelectItem key={term} value={term}>
                          {term}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Academic Year</Label>
                  <Input
                    value={formData.academic_year}
                    onChange={(e) =>
                      setFormData({ ...formData, academic_year: e.target.value })
                    }
                    placeholder="2025"
                    required
                    data-testid="result-year"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-pink-500 hover:bg-pink-600"
                  data-testid="save-result-btn"
                >
                  Add Result
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-end">
        <div className="flex-1">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              placeholder="Search by student name, ID, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="result-search"
            />
          </div>
        </div>
        {user?.role === "admin" && (
          <>
            <div className="w-full sm:w-48">
              <Label className="text-sm mb-2 block">Filter by Class</Label>
              <div className="flex gap-2">
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.display_name || `${cls.grade_level} ${cls.name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedClass && (
                  <Button
                    onClick={() => setSelectedClass("")}
                    variant="ghost"
                    size="sm"
                    className="text-slate-500 hover:text-red-500"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            <Button
              onClick={handlePrint}
              variant="outline"
              className="border-pink-300 text-pink-600 hover:bg-pink-50"
            >
              Print Results
            </Button>
          </>
        )}
      </div>

      {/* Results Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">
            All Results ({filteredResults.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileSpreadsheet size={48} className="mx-auto mb-4 opacity-50" />
              <p>No results found</p>
              <p className="text-sm">Click "Add Result" to enter a result</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-medium text-slate-500">Student ID</TableHead>
                    <TableHead className="font-medium text-slate-500">Student Name</TableHead>
                    <TableHead className="font-medium text-slate-500">Subject</TableHead>
                    <TableHead className="font-medium text-slate-500">Score</TableHead>
                    <TableHead className="font-medium text-slate-500">Percentage</TableHead>
                    <TableHead className="font-medium text-slate-500">Grade</TableHead>
                    <TableHead className="font-medium text-slate-500">Term</TableHead>
                    <TableHead className="font-medium text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredResults.map((result) => (
                    <TableRow
                      key={result.id}
                      className="table-row-hover"
                      data-testid={`result-row-${result.id}`}
                    >
                      <TableCell className="font-mono text-slate-600">
                        {result.student_id_code}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {result.student_name}
                      </TableCell>
                      <TableCell className="text-slate-600">{result.subject}</TableCell>
                      <TableCell className="text-slate-600">
                        {result.score}/{result.max_score}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {result.percentage}%
                      </TableCell>
                      <TableCell>
                        <Badge className={getGradeColor(getUnitGrade(result.percentage))}>
                          {getUnitGrade(result.percentage)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {result.term} ({result.academic_year})
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(result.id)}
                          className="text-slate-500 hover:text-red-500"
                          data-testid={`delete-result-${result.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Class Rankings Section - Admin Only */}
      <Card className="border-slate-200 mt-6" id="class-rankings-section">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Trophy size={20} className="text-amber-500" />
              Class Rankings
            </CardTitle>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs mb-1 block">Class</Label>
                <Select value={rankingsClass} onValueChange={(val) => {
                  setRankingsClass(val);
                  if (val && rankingsTerm) fetchClassRankings(val, rankingsTerm);
                }}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>{cls.display_name || `${cls.grade_level} ${cls.name}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Term</Label>
                <Select value={rankingsTerm} onValueChange={(val) => {
                  setRankingsTerm(val);
                  if (rankingsClass && val) fetchClassRankings(rankingsClass, val);
                }}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Select term" />
                  </SelectTrigger>
                  <SelectContent>
                    {TERMS.map((term) => (
                      <SelectItem key={term} value={term}>{term}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {rankingsData.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-pink-300 text-pink-600 hover:bg-pink-50"
                  onClick={() => {
                    const cls = classes.find(c => c.id === rankingsClass);
                    const className = cls?.display_name || `${cls?.grade_level || ''} ${cls?.name || ''}`.trim() || '';
                    const rows = rankingsData.map((s) => {
                      const grade = s.average_percentage >= 90 ? 1 : s.average_percentage >= 80 ? 2 : s.average_percentage >= 70 ? 3 : s.average_percentage >= 60 ? 4 : s.average_percentage >= 50 ? 5 : 6;
                      const posLabel = s.position === 1 ? '1st' : s.position === 2 ? '2nd' : s.position === 3 ? '3rd' : s.position;
                      return `<tr>
                        <td class="pos">${posLabel}</td>
                        <td class="mono">${s.student_id_code || ''}</td>
                        <td>${s.first_name} ${s.last_name}</td>
                        <td>${s.gender || 'N/A'}</td>
                        <td class="right">${s.subject_count}</td>
                        <td class="right mono">${s.total_score}</td>
                        <td class="right mono">${s.average_percentage}%</td>
                        <td>Unit ${grade}</td>
                      </tr>`;
                    }).join('');
                    const win = window.open('', '_blank');
                    win.document.write(`<!DOCTYPE html>
                      <html><head><title>Class Rankings - ${className} - ${rankingsTerm}</title>
                      <style>
                        body { font-family: Arial, sans-serif; padding: 30px; color: #1e293b; }
                        h1 { font-size: 20px; margin-bottom: 4px; }
                        h2 { font-size: 14px; color: #666; margin-bottom: 16px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                        th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; font-size: 13px; }
                        th { background: #f5f5f5; font-weight: 600; }
                        .pos { text-align: center; font-weight: bold; }
                        .mono { font-family: monospace; }
                        .right { text-align: right; }
                        .footer { margin-top: 30px; font-size: 11px; color: #999; text-align: center; }
                      </style></head><body>
                      <h1>${className} — Class Rankings</h1>
                      <h2>${rankingsTerm} • ${new Date().getFullYear()}</h2>
                      <table>
                        <thead><tr>
                          <th class="pos">Pos</th><th>Student ID</th><th>Name</th><th>Gender</th>
                          <th class="right">Subjects</th><th class="right">Total</th><th class="right">Average %</th><th>Grade</th>
                        </tr></thead>
                        <tbody>${rows}</tbody>
                      </table>
                      <div class="footer">Printed on ${new Date().toLocaleDateString()} — Cresabel School</div>
                      </body></html>`);
                    win.document.close();
                    win.focus();
                    setTimeout(() => { win.print(); }, 300);
                  }}
                >
                  <Printer size={16} className="mr-1" /> Print
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!rankingsClass || !rankingsTerm ? (
            <div className="text-center py-8 text-slate-500">
              <Trophy size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a class and term to view rankings</p>
            </div>
          ) : rankingsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            </div>
          ) : rankingsData.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p className="text-sm">No results found for this class and term</p>
            </div>
          ) : (
            <div id="rankings-print-area" className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-medium text-slate-500 w-16">Pos</TableHead>
                    <TableHead className="font-medium text-slate-500">Student ID</TableHead>
                    <TableHead className="font-medium text-slate-500">Name</TableHead>
                    <TableHead className="font-medium text-slate-500">Gender</TableHead>
                    <TableHead className="font-medium text-slate-500">Subjects</TableHead>
                    <TableHead className="font-medium text-slate-500">Total</TableHead>
                    <TableHead className="font-medium text-slate-500">Average %</TableHead>
                    <TableHead className="font-medium text-slate-500">Grade</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rankingsData.map((s) => (
                    <TableRow key={s.id} className="table-row-hover">
                      <TableCell className="font-bold text-slate-900 text-center pos">
                        {s.position === 1 ? '🥇' : s.position === 2 ? '🥈' : s.position === 3 ? '🥉' : s.position}
                      </TableCell>
                      <TableCell className="font-mono text-slate-600 text-sm mono">
                        {s.student_id_code}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {s.first_name} {s.last_name}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {s.gender || 'N/A'}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {s.subject_count}
                      </TableCell>
                      <TableCell className="font-mono text-slate-900">
                        {s.total_score}
                      </TableCell>
                      <TableCell className="font-mono font-bold text-slate-900">
                        {s.average_percentage}%
                      </TableCell>
                      <TableCell>
                        <Badge className={getGradeColor(getUnitGrade(s.average_percentage))}>
                          Unit {getUnitGrade(s.average_percentage)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
